import { useEffect, useState } from "react";
import { Errors } from "services/forms/validation/types";
import { validateYup } from "services/forms/validation/validation";

import { Modal, SuccessOverlay } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { Wizard } from "ui/components/Wizard";
import WizardStep from "ui/components/Wizard/WizardStep";
import WizardFooter from "ui/components/Wizard/WizardFooter";
import successAnimation from "ui/theme/animations/success.json";
import { CALCULATION_CREATE_PAGE_TITLE } from "ui/components/PageTitle/consts";

import { FleetVehicleEditFormValues } from "services/api/fleet/vehicles/types";
import { initialFleetVehicleEditForm } from "services/api/fleet/vehicles/consts";
import VehiclePickerForm from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleEditCard/components/VehiclePickerForm/VehiclePickerForm";
import { VehicleInfo } from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleEditCard/components/VehicleSummaryCard/types";
import { initialVehicleInfo } from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleEditCard/consts";
import { getVehicleTypeAPI } from "services/api/catalog/vehicles/types/api";

import { CalculationEditFormValues } from "services/api/calculations/types";
import { initialCalculationCreateFormValues } from "services/api/calculations/consts";
import { createCalculationAPI } from "services/api/calculations/api";

import { CalculationEditCardProps } from "./types";
import {
  CALCULATION_PICKER_SCHEMA,
  CALCULATION_PERIOD_SCHEMA,
  CALCULATION_NAME_SCHEMA,
} from "./validation";
import CalculationPeriodForm from "./components/CalculationPeriodForm/CalculationPeriodForm";
import { CalculationDetailCard } from "./components/CalculationDetailCard";
import { CalculationSaveModal } from "./components/CalculationSaveModal";

const CalculationEditCard: React.FC<CalculationEditCardProps> = (
  props
) => {
  const {
    show,
    onClose,
    onSuccess,
    vehicleManufacturerId,
    vehicleModelId,
    vehicleTypeId,
  } = props;

  const hasInitialType = !!(
    vehicleManufacturerId &&
    vehicleModelId &&
    vehicleTypeId
  );

  const [stepCurrent, setStepCurrent] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [pickerErrors, setPickerErrors] = useState<Errors>({});
  const [pickerFormValues, setPickerFormValues] =
    useState<FleetVehicleEditFormValues>(initialFleetVehicleEditForm);
  const [vehicleInfo, setVehicleInfo] =
    useState<VehicleInfo>(initialVehicleInfo);
  const [formValues, setFormValues] = useState<CalculationEditFormValues>(
    initialCalculationCreateFormValues
  );
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);

  // Reset all state when modal opens
  useEffect(() => {
    if (!show) return;

    setStepCurrent(hasInitialType ? 1 : 0);
    setErrors({});
    setPickerErrors({});
    setIsLoading(false);
    setShowSuccessOverlay(false);
    setShowSaveModal(false);
    setFormValues({
      ...initialCalculationCreateFormValues,
      vehicleManufacturerId: vehicleManufacturerId ?? "",
      vehicleModelId: vehicleModelId ?? null,
      vehicleTypeId: vehicleTypeId ?? null,
    });
    setPickerFormValues({
      ...initialFleetVehicleEditForm,
      vehicleManufacturerId: vehicleManufacturerId ?? "",
      vehicleModelId: vehicleModelId ?? null,
      vehicleTypeId: vehicleTypeId ?? null,
    });
    if (!hasInitialType) {
      setVehicleInfo(initialVehicleInfo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // If vehicle type was passed in, fetch its data for step 3
  // (step 1 / VehiclePickerForm is skipped, so vehicleInfo is not populated automatically)
  useEffect(() => {
    if (!show || !hasInitialType) return;

    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const type = await getVehicleTypeAPI(
          vehicleManufacturerId as string,
          vehicleModelId as string,
          vehicleTypeId as string
        );
        if (cancelled) return;
        // NOTE: vehicleManufacturerName / vehicleModelName are not populated here — a future caller passing the type IDs should also supply those, or step 3's heading renders blank.
        setVehicleInfo((prev) => ({
          ...prev,
          vehicleTypeName: type.name ?? null,
          vehicleImage: type.image ?? null,
          fuelType: type.fuelType?.name ?? null,
          transmission: type.transmission?.name ?? null,
          maximumPowerHp: type.maximumPowerHp ?? null,
          maximumPowerKw: type.maximumPowerKw ?? null,
          cylinderCapacity: type.cylinderCapacity ?? null,
          numberOfDoors: type.numberOfDoors ?? null,
          seats: type.seats ?? null,
          jom: type.jom ?? null,
        }));
      } catch {
        // error is surfaced via snackbar inside createApiCall
      }
      if (!cancelled) setIsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const handleNextStep = () => {
    if (stepCurrent === 0) {
      const isValid = validateYup(
        pickerFormValues,
        CALCULATION_PICKER_SCHEMA,
        setPickerErrors
      );
      if (!isValid) return;

      setFormValues((prev) => ({
        ...prev,
        vehicleManufacturerId: pickerFormValues.vehicleManufacturerId ?? "",
        vehicleModelId: pickerFormValues.vehicleModelId,
        vehicleTypeId: pickerFormValues.vehicleTypeId,
      }));
      setStepCurrent(1);
      return;
    }

    if (stepCurrent === 1) {
      const isValid = validateYup(
        formValues,
        CALCULATION_PERIOD_SCHEMA,
        setErrors
      );
      if (!isValid) return;
      setStepCurrent(2);
    }
  };

  const handleBackStep = () => {
    setStepCurrent((s) => Math.max(s - 1, hasInitialType ? 1 : 0));
  };

  // Footer "Spremi" — opens the save modal where the calculation name is entered
  const handleOpenSaveModal = () => setShowSaveModal(true);

  const handleCloseSaveModal = () => {
    setShowSaveModal(false);
    setErrors({});
  };

  // Save modal "Spremi" — creates the offer and saves the calculation
  const handleFinish = async () => {
    const isValid = validateYup(formValues, CALCULATION_NAME_SCHEMA, setErrors);
    if (!isValid) return;

    setIsLoading(true);
    try {
      const result = await createCalculationAPI(formValues);
      if (result) {
        setShowSaveModal(false);
        setShowSuccessOverlay(true);
      }
    } catch {
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleSuccessDismiss = () => {
    setShowSuccessOverlay(false);
    onSuccess?.();
    onClose();
  };

  const steps = [
    <WizardStep key={0} stepNumber={0} stepCurrent={stepCurrent}>
      <div className="flex items-center justify-center px-4 h-full">
        <div className="w-full max-w-4xl">
          <VehiclePickerForm
            formValues={pickerFormValues}
            setFormValues={setPickerFormValues}
            errors={pickerErrors}
            setErrors={setPickerErrors}
            vehicleInfo={vehicleInfo}
            setVehicleInfo={setVehicleInfo}
            showHeader
            title="Nova kalkulacija"
            description="Izaberite vozilo za koje želite izraditi kalkulaciju klikom na izbornik ispod, zatim nastavite klikom na gumb naprijed."
          />
        </div>
      </div>
    </WizardStep>,
    <WizardStep key={1} stepNumber={1} stepCurrent={stepCurrent}>
      <CalculationPeriodForm
        formValues={formValues}
        setFormValues={setFormValues}
        errors={errors}
      />
    </WizardStep>,
    <WizardStep key={2} stepNumber={2} stepCurrent={stepCurrent}>
      <CalculationDetailCard
        vehicleInfo={vehicleInfo}
        formValues={formValues}
        setFormValues={setFormValues}
      />
    </WizardStep>,
  ];

  return (
    <>
      <Modal show={show} onHide={onClose} size="full" position="side">
        <ModalHeader
          icon="/media/images/add-icon.svg"
          title={CALCULATION_CREATE_PAGE_TITLE.text}
          onHide={onClose}
        />
        <ModalBody>
          <Wizard steps={steps} />
        </ModalBody>
        <WizardFooter
          stepCurrent={stepCurrent}
          steps={steps}
          onNextStep={handleNextStep}
          onBackStep={handleBackStep}
          onFinish={handleOpenSaveModal}
          isLoading={isLoading}
          isReadOnly={false}
        />
      </Modal>
      <CalculationSaveModal
        show={showSaveModal}
        onClose={handleCloseSaveModal}
        formValues={formValues}
        setFormValues={setFormValues}
        errors={errors}
        onSave={handleFinish}
        isLoading={isLoading}
      />
      <SuccessOverlay
        show={showSuccessOverlay}
        animation={successAnimation}
        title="Kalkulacija je uspješno spremljena!"
        description="Možete ju pregledati na popisu kalkulacija ili kreirati novu."
        onDismiss={handleSuccessDismiss}
      />
    </>
  );
};

export default CalculationEditCard;
